import { Link } from "react-router-dom";
import { Facebook, Instagram, Twitter, Linkedin, Youtube, Globe, Mail, Phone, MapPin, ArrowUpRight } from "lucide-react";
import { useSettings } from "@/context/SettingsContext";
import { Logo } from "@/components/Logo";

const SOCIAL_ICONS = { facebook: Facebook, instagram: Instagram, x: Twitter, linkedin: Linkedin, youtube: Youtube };

export const Footer = () => {
  const { settings } = useSettings();
  const { brand, contact, menu, social } = settings || {};
  const items = (menu || []).filter((m) => m && m.enabled !== false);
  const liveSocials = (social || []).filter((s) => s.url);

  return (
    <footer className="border-t border-white/5 bg-[#090d14]" data-testid="site-footer">
      <div className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-12 lg:py-20">
        <div className="grid grid-cols-1 gap-12 lg:grid-cols-12">
          <div className="lg:col-span-5">
            <Link to="/" data-testid="footer-logo-link" className="inline-flex items-center gap-3">
              <Logo />
            </Link>
            <p className="mt-6 max-w-sm font-display text-2xl font-bold leading-snug text-white sm:text-3xl">
              {brand?.tagline || "Changing the world for you!"}
            </p>
            <Link
              to="/contact"
              data-testid="footer-cta-link"
              className="mt-8 inline-flex items-center gap-2 rounded-full border border-cyan-400/40 px-6 py-3 text-sm font-semibold text-cyan-300 transition hover:bg-cyan-400/10"
            >
              Start a project <ArrowUpRight className="h-4 w-4" />
            </Link>
          </div>

          <div className="lg:col-span-3">
            <p className="font-mono text-xs uppercase tracking-[0.25em] text-slate-500">Explore</p>
            <ul className="mt-5 space-y-3">
              {items.map((m) => (
                <li key={m.path + m.label}>
                  <Link to={m.path} className="text-sm text-slate-300 transition hover:text-cyan-300" data-testid={`footer-${m.label.toLowerCase().replace(/\s+/g, "-")}-link`}>
                    {m.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div className="lg:col-span-4">
            <p className="font-mono text-xs uppercase tracking-[0.25em] text-slate-500">Get in touch</p>
            <ul className="mt-5 space-y-3 text-sm text-slate-300">
              {contact?.phone && (
                <li className="flex items-center gap-3">
                  <Phone className="h-4 w-4 text-cyan-400" />
                  <a href={`tel:${contact.phone.replace(/\s+/g, "")}`} className="transition hover:text-cyan-300" data-testid="footer-phone-link">{contact.phone}</a>
                </li>
              )}
              {contact?.email && (
                <li className="flex items-center gap-3">
                  <Mail className="h-4 w-4 text-cyan-400" />
                  <a href={`mailto:${contact.email}`} className="transition hover:text-cyan-300" data-testid="footer-email-link">{contact.email}</a>
                </li>
              )}
              {contact?.address && (
                <li className="flex items-start gap-3">
                  <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-cyan-400" />
                  <span>{contact.address}</span>
                </li>
              )}
            </ul>
            {liveSocials.length > 0 && (
              <div className="mt-6 flex gap-3">
                {liveSocials.map((s) => {
                  const Icon = SOCIAL_ICONS[s.platform?.toLowerCase()] || Globe;
                  return (
                    <a
                      key={s.platform + s.url}
                      href={s.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label={s.platform}
                      data-testid={`footer-social-${s.platform?.toLowerCase()}-link`}
                      className="rounded-full border border-white/10 p-2.5 text-slate-300 transition hover:border-cyan-400/50 hover:text-cyan-300"
                    >
                      <Icon className="h-4 w-4" />
                    </a>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        <div className="mt-16 flex flex-col items-start justify-between gap-4 border-t border-white/5 pt-8 sm:flex-row sm:items-center">
          <p className="text-xs text-slate-500">© {new Date().getFullYear()} {brand?.company_name || "Cyber Planet"} Inc. All rights reserved.</p>
          <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-slate-600">Digital · Media · Events</p>
        </div>
      </div>
    </footer>
  );
};
